import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Store, CheckCircle, XCircle, Eye, Edit, Trash2 } from 'lucide-react'
import useMerchantStore from '@/stores/merchantStore'
import { Merchant } from '@/services/merchantService'
import { DetailHeader } from '@/components/ui/DetailHeader'
import DataTable from '@/components/ui/DataTable'
import FilterBar from '@/components/ui/FilterBar'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { getImageUrl } from '@/config/imageConfig'

const MerchantList = () => {
  const {
    merchants,
    loading,
    error,
    pagination,
    fetchMerchants,
    deleteMerchant,
    setFilters
  } = useMerchantStore()

  const navigate = useNavigate()

  useEffect(() => {
    fetchMerchants()
  }, [])

  // Extract unique business types for filter options
  const businessTypes = useMemo(() => {
    const types = Array.from(new Set(merchants.map(m => m.business_type).filter(Boolean)))
    return types.map(type => ({ value: type, label: type }))
  }, [merchants])

  const handleDelete = async (id: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir désactiver ce commerçant ?')) {
      return
    }
    
    try {
      await deleteMerchant(id)
    } catch (err: any) {
      console.error('Error deleting merchant:', err)
      alert(err.response?.data?.error || 'Erreur lors de la suppression')
    }
  }

  const handleToggleStatus = async (_merchant: Merchant) => {
    try {
      // This would require an update endpoint in the store
      alert('Fonctionnalité à implémenter: modification du statut')
    } catch (err: any) {
      console.error('Error toggling merchant status:', err)
      alert(err.response?.data?.error || 'Erreur lors de la modification du statut')
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const columns = useMemo(() => [
    {
      key: 'business_name',
      label: 'Commerçant',
      sortable: true,
      render: (_value: any, row: Merchant) => (
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10">
            {row?.logo_url ? (
              <img
                src={getImageUrl(`/uploads/merchants/${row.logo_url}`)}
                alt={row?.business_name || 'Logo commerçant'}
                className="h-10 w-10 rounded-full object-cover border border-gray-200"
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                  const fallback = e.currentTarget.nextElementSibling as HTMLElement | null
                  if (fallback) fallback.style.display = 'flex'
                }}
              />
            ) : null}
            <div
              className="h-10 w-10 rounded-full bg-primary-100 items-center justify-center"
              style={{ display: row?.logo_url ? 'none' : 'flex' }}
            >
              <Store className="h-6 w-6 text-primary-600" />
            </div>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {row?.business_name || 'Nom inconnu'}
            </div>
            <div className="text-sm text-gray-500">
              {row?.business_description || 'Aucune description'}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'organization_name',
      label: 'Organisation',
      sortable: true,
      render: (_value: any, row: Merchant) => (
        <div className="text-sm text-gray-900">
          {row?.organization_name || 'Aucune organisation'}
          {row?.store_code && (
            <div className="text-xs text-gray-500">
              Code: {row.store_code}
            </div>
          )}
          {row?.parent_store_name && (
            <div className="text-xs text-gray-500">
              Parent: {row.parent_store_name}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'business_type',
      label: 'Type',
      sortable: true,
      render: (value: string) => (
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
          {value}
        </Badge>
      )
    },
    {
      key: 'email',
      label: 'Contact',
      sortable: true,
      render: (value: string) => (
        <div className="text-sm text-gray-900">{value}</div>
      )
    },
    {
      key: 'is_active',
      label: 'Statut',
      sortable: true,
      render: (value: any, row: Merchant) => {
        const isActive = row?.is_active ?? value
        return isActive ? (
          <Badge variant="default" className="inline-flex items-center bg-green-100 text-green-800 border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Actif
          </Badge>
        ) : (
          <Badge variant="destructive" className="inline-flex items-center">
            <XCircle className="h-3 w-3 mr-1" />
            Inactif
          </Badge>
        )
      }
    },
    {
      key: 'created_at',
      label: 'Créé le',
      sortable: true,
      render: (value: string) => (
        <div className="text-sm text-gray-900">
          {formatDate(value)}
        </div>
      )
    }
  ], [])

  const actions = useMemo(() => [
    {
      label: 'Voir',
      icon: <Eye className="h-4 w-4" />,
      onClick: (row: Merchant) => row?.merchant_id && navigate(`/merchants/${row.merchant_id}`)
    },
    {
      label: 'Modifier',
      icon: <Edit className="h-4 w-4" />,
      onClick: (row: Merchant) => row?.merchant_id && navigate(`/merchants/${row.merchant_id}/edit`)
    },
    {
      label: 'Activer/Désactiver',
      icon: <CheckCircle className="h-4 w-4" />,
      onClick: (row: Merchant) => row && handleToggleStatus(row)
    },
    {
      label: 'Supprimer',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: (row: Merchant) => row?.merchant_id && handleDelete(row.merchant_id)
    }
  ], [navigate])

  const filterOptions = useMemo(() => [
    {
      key: 'search',
      label: 'Recherche',
      type: 'text' as const,
      placeholder: 'Nom, description, email...'
    },
    {
      key: 'business_type',
      label: 'Type de commerce',
      type: 'select' as const,
      options: [
        { value: '', label: 'Tous les types' },
        ...businessTypes
      ]
    },
    {
      key: 'is_active',
      label: 'Statut',
      type: 'select' as const,
      options: [
        { value: 'all', label: 'Tous les statuts' },
        { value: 'true', label: 'Actif' },
        { value: 'false', label: 'Inactif' }
      ]
    }
  ], [businessTypes])

  const handleSearch = (value: string) => {
    setFilters({ search: value })
    fetchMerchants({ search: value })
  }

  const handleFilterChange = (filters: any[]) => {
    const filterObj: any = {}
    filters.forEach(filter => {
      if (filter.key === 'is_active' && filter.value !== 'all') {
        filterObj.is_active = filter.value === 'true'
      } else if (filter.key === 'business_type' && filter.value) {
        filterObj.business_type = filter.value
      }
    })
    
    setFilters(filterObj)
    fetchMerchants(filterObj)
  }

  const handlePageChange = (page: number) => {
    setFilters({ page })
    fetchMerchants({ page })
  }

  const handlePageSizeChange = (newSize: number) => {
    setFilters({ limit: newSize, page: 1 })
    fetchMerchants({ limit: newSize, page: 1 })
  }

  const breadcrumbs = [
    { label: 'Tableau de bord', href: '/' },
    { label: 'Commerçants', href: '/merchants' }
  ]

  const headerActions = [
    {
      label: 'Nouveau Commerçant',
      icon: <Plus className="h-4 w-4 mr-2" />,
      onClick: () => navigate('/merchants/new'),
      variant: 'default' as const
    }
  ]

  return (
    <div className="space-y-6">
      <DetailHeader
        title="Commerçants"
        subtitle="Gérez tous les commerçants de la plateforme"
        breadcrumbs={breadcrumbs}
        actions={headerActions}
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
          <div className="mt-4">
            <Button variant="outline" onClick={() => fetchMerchants()}>
              Réessayer
            </Button>
          </div>
        </Alert>
      )}

      <FilterBar
        searchPlaceholder="Rechercher par nom, description, email..."
        filters={filterOptions}
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
      />

      <DataTable
        columns={columns}
        data={merchants}
        actions={actions}
        loading={loading}
        emptyMessage="Aucun commerçant trouvé"
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit || 10,
          total: pagination.total,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange
        }}
      />
    </div>
  )
}

export default MerchantList
